import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  ViewChildren,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  findActionByNameAndValue,
  findConditionByNameAndValue,
  RulesOperator,
  RulesTag,
  RulesAmountType,
  RulesServiceLevel,
  RulesProviders,
  RulesProviderServices,
  RulesActions,
  RulesConditions,
  handleColorCodingBackdropClick,
} from '@app/features/automation-rules/utils';
import { ServiceLevelDisplayNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { initShopTimeZone } from 'src/app/pages/checkout-app/utils';
import { Shop } from '@app/services/shops/shop.model';
import { AutomationRulesActions } from '../../../actions';
import { SelectedRuleParams } from '../rule-template-selector.component';
import { ShopService } from '@app/services/shops/shop.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { of, Subject, Subscription } from 'rxjs';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { debounceTime, switchMap, tap, first, takeUntil, finalize, catchError } from 'rxjs/operators';
import { AutomationRulesService } from '@app/features/automation-rules/automation-rules.service';
import { RuleTemplateService } from '../rule-template.service';
import {
  DeliveryMethodType,
  FilterEntities,
  SkusResponseType,
  TagResponseType,
} from '@pages/workspace/assign/components/advanced-filters/config/models';
import { currencyStrToSign, generateTimeOptions } from 'src/utils';
import { Carrier, ColorCoding, ColorEnum, ReturnPreferences } from '@pdqdev/pdq-shared/dist/entities';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'order-assigned-template',
  templateUrl: './order-assigned-template.component.html',
  styleUrls: ['./order-assigned-template.component.scss'],
  standalone: false,
})
export class OrderAssignedTemplateComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() conditions: Array<any>;
  @Input() actions: Array<any>;
  @Input() selectedRuleParams?: SelectedRuleParams;

  @Input() onChangeConditionSelect: EventEmitter<any>;
  @Input() onChangeActionSelect: EventEmitter<any>;
  @Input() onChangeOperatorSelect: EventEmitter<any>;
  @Input() onChangeOperator2Select: EventEmitter<any>;
  @Input() onRemoveCondition: EventEmitter<any>;
  @ViewChildren('conditionSelect') conditionSelects;
  @ViewChildren('selectList') selectList;
  @ViewChildren('tagsInput') tagsInputs;
  @ViewChildren('skusInput') skusInputs;

  readonly RulesAmountType = RulesAmountType;

  shopSettings = {};
  openLastMatOption: boolean = false;
  isLoadingResults: boolean = false;
  destroyed$ = new Subject();
  deliveryMethodsControlsSubscriptions$: Subscription[] = [];
  deliveryMethods = [];
  tagsControlsSubscriptions$: Subscription[] = [];
  skusControlsSubscriptions$: Subscription[] = [];
  inventoryLocationOptions: DropdownOption[];
  shipOnDateOptions: DropdownOption[];
  isProviderEqualToCarrier: boolean = false;
  packagesOptions: DropdownOption[][][] = [[[null]]];
  isFiltersOptions: DropdownOption[][][] = [[[null]]];
  isNotFiltersOptions: DropdownOption[][][] = [[[null]]];
  deliveryMethodOptions: DropdownOption[][][] = [[[null]]];
  deliveryDateOptions: DropdownOption[][] = [[null]];
  createdWeekDaysOptions: DropdownOption[][][] = [[[null]]];
  timeWindowWeekDaysOptions: DropdownOption[][][] = [[[null]]];
  timeWindowOfDayOptions = generateTimeOptions();
  timeOfDayOptions = generateTimeOptions();
  headsUp: string[] = [];
  shopTimeZone: string;
  handleColorCodingBackdropClick = handleColorCodingBackdropClick;

  // ---Conditions---
  currentOperatorValue: RulesOperator[][] = [[null]];
  currentOperator2Value: RulesOperator[][] = [[null]];
  currentAllConditionsSelectValue: AutomationRulesActions.SelectedCondition[][] = [[null]];
  currentDeliveryMethodsSelectValue: DropdownOption[][][] = [[[]]];
  currentSelectedDeliveryMethods: string[][][] = [[[]]];
  controls: UntypedFormControl[][] = [[new UntypedFormControl()]];
  tags: RulesTag[] = [];
  currentSelectedTags: RulesTag[][][] = [[[]]];
  skus: string[] = [];
  currentSelectedSkus: string[][][] = [[[]]];
  currentWeightValue: number[][] = [[null]];
  currentTotalValueVal: number[][] = [[]];
  currentItemQuantityValue: number[][] = [[null]];
  currentInventoryLocationValue: DropdownOption[][] = [[null]];
  currentPackageValue: DropdownOption[][][] = [[[null]]];
  currentIsFilterValue: DropdownOption[][][] = [[[null]]];
  currentIsNotFilterValue: DropdownOption[][][] = [[[null]]];
  currentTagsValue: Array<string>[] = [[null]];
  currentSkusValue: Array<string>[] = [[null]];
  currentCreatedOnValue: Array<
    Array<{
      weekDays: DropdownOption[];
      from: DropdownOption;
      to: DropdownOption;
      timezone: string;
    }>
  > = [[null]];
  currentTimeWindowValue: Array<
    Array<{
      weekDays: DropdownOption[];
      from: DropdownOption;
      to: DropdownOption;
      timezone: string;
    }>
  > = [[null]];
  currentStateSelectValue: Array<Array<string | null>> = [[null]];
  currentShipOnDateValue: DropdownOption[][] = [[null]];
  autoCompleteCache = new Map<string, TagResponseType | SkusResponseType | DeliveryMethodType>();

  // ---Action---
  currentActionSelectValue: AutomationRulesActions.SelectedAction;
  currentDeliveryServiceSelectValue: string | DropdownOption;
  currentServiceLevelSelectValue: string;
  deliveryServiceLevels: Array<RulesServiceLevel>;
  currentShippingDays: number;
  currentProvidersSelectValue: DropdownOption[];
  currentProviderServiceSelectValue: DropdownOption[];
  deliveryServiceProviders: Array<RulesProviders>;
  providerServices: Array<RulesProviderServices>;
  currentBatchUpToSelectValue: string = 'all';
  currentBatchUpToX: number = 1;
  currentWeekDaysValue: Array<string> = [];
  weekDays: Array<string> = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  currentTimePickerValue: string = '8:00 AM';
  currentSnoozeSelectValue: any = {};
  currentShipOnValue: any = {};
  currentRecommendedCarrier: any = {};
  currentSmartShipOn: any = {};
  currentTeamNote: any = {};
  currentActionAddTagsValue: string[] = [];
  currentActionDeleteTagsValue: string[] = [];
  currentShipmentExtras: any = {};
  currentColorCoding: string[] = [ColorEnum.NO_COLOR];
  currentColorCodingOperator: DropdownOption;
  colorCodingOptions: DropdownOption[] = [];
  carriersData;
  colorCodingData: ColorCoding[];
  shopCarriers: Carrier[];
  splitOrderData: any;
  initialReturnPreferences: Partial<ReturnPreferences> = { active: true };
  snoozeFormatter = (x) => x?.toString()?.replace('[[x]]', '<span style="color: var(--on-surface-light)">x</span>');
  deliveryMethod = '';
  batchName: string;

  indexOfLastMatOption: any;
  currentDeliveryMethodInputValue: string[][] = [[null]];

  constructor(
    private shopService: ShopService,
    private automationRulesService: AutomationRulesService,
    private changeDetectorRef: ChangeDetectorRef,
    private ruleTemplateService: RuleTemplateService,
  ) {}

  ngOnInit() {
    this.initListeners();
    this.initDropDownOptions();
    this.initSelectedRuleParams();
    this.initShopSettings();
    this.initSearchTerm([0, 0], FilterEntities.TAG);
    this.initSearchTerm([0, 0], FilterEntities.SKU);
    this.initSearchTerm([0, 0], FilterEntities.DELIVERY_METHOD);
  }

  ngAfterViewInit() {
    this.selectList.changes.subscribe(() => {
      this.conditionSelects.forEach((condition: MatSelect) => {
        if (!this.indexOfLastMatOption) {
          return;
        }
        const [j, i] = this.indexOfLastMatOption;
        const atrb = condition._elementRef.nativeElement.getAttribute('data-2dIndex');
        if (condition._elementRef.nativeElement.getAttribute('data-2dIndex') == j + ',' + i) {
          this.indexOfLastMatOption = null;
          condition.open();
          this.changeDetectorRef.detectChanges();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  initListeners() {
    this.onChangeActionSelect.pipe(takeUntil(this.destroyed$)).subscribe((e) => this.setHeadsUpNotifications(e));
  }

  initSelectedRuleParams() {
    this.currentAllConditionsSelectValue = this.getSelectedConditions();
    this.currentActionSelectValue = this.getSelectedAction();

    if (this.currentActionSelectValue) {
      const selectedAction = { ...this.currentActionSelectValue };
      if (this.currentDeliveryServiceSelectValue && selectedAction.name === 'batchImmediatelyAndAssign') {
        this.selectDeliveryService({
          value: this.currentDeliveryServiceSelectValue,
        });
      } else if (this.currentDeliveryServiceSelectValue && selectedAction.name === 'batchOnSpecificTimeAndAssign') {
        this.selectDeliveryServiceOnSpecificTime({
          value: this.currentDeliveryServiceSelectValue,
        });
      } else if (selectedAction.name == RulesActions.ColorCoding && this.currentColorCoding) {
        this.onColorCodingChanged(this.currentColorCoding, this.currentColorCodingOperator);
      } else if (this.currentShipmentExtras?.shipmentExtrasFields) {
        this.onShipmetExtrasChanged(this.currentShipmentExtras?.shipmentExtrasFields);
      } else if (selectedAction.name == RulesActions.SplitOrderByStrategy) {
        const selectedAction = {
          ...this.currentActionSelectValue,
        };
        selectedAction.value = this.splitOrderData;
        this.onChangeActionSelect.emit(selectedAction);
      } else if (this.currentTeamNote?.message) {
        this.onTeamNoteChanged(this.currentTeamNote);
      } else if (this.deliveryMethod) {
        this.onDeliveryMethodChanged(this.deliveryMethod);
      } else if (selectedAction.name === RulesActions.SetRecommendedCarrier) {
        this.onShipmentPreferencesChanged(this.currentRecommendedCarrier);
      } else if (selectedAction.name === RulesActions.AddTags) {
        this.tagsChanged(this.currentActionAddTagsValue, 'add');
      } else if (selectedAction.name === RulesActions.DeleteTags) {
        this.tagsChanged(this.currentActionDeleteTagsValue, 'delete');
      } else {
        this.onChangeActionSelect.emit(selectedAction);
      }
    }
  }

  initShopSettings() {
    this.shopService.shop$.pipe(first()).subscribe((shop: Shop) => {
      this.shopSettings = {
        shopTimeZone: initShopTimeZone(shop),
        currency: currencyStrToSign(shop.metadata?.currency),
        weightUnits: shop.metadata?.weightUnits,
      };
      this.colorCodingData = shop.colorCoding;
      this.shopTimeZone = shop.metadata?.ianaTimeZone;
    });
  }

  initSearchTerm(indices: [number, number], type: FilterEntities): void {
    const [j, i] = indices;
    let fieldName;
    switch (type) {
      case FilterEntities.TAG:
        fieldName = 'tags';
        break;
      case FilterEntities.SKU:
        fieldName = 'skus';
        break;
      case FilterEntities.DELIVERY_METHOD:
        fieldName = 'deliveryMethods';
        break;
      default:
        fieldName = 'tags';
    }
    this[fieldName] = [];
    let subscription;
    const autoCompleteRes$ = this.controls[j][i].valueChanges.pipe(
      debounceTime(500),
      tap(() => (this.isLoadingResults = true)),

      switchMap((value) => {
        const trimmedValue = value?.trim();
        if (trimmedValue?.length > 0) {
          {
            if (this.autoCompleteCache.has(trimmedValue + '_' + type)) {
              this.isLoadingResults = false;
              return of(this.autoCompleteCache.get(trimmedValue + '_' + type));
            }
          }

          return this.automationRulesService.getAutoComplete(trimmedValue, type).pipe(
            tap((response) => {
              this.isLoadingResults = false;
              this.autoCompleteCache.set(trimmedValue + '_' + type, response);
            }),
            catchError((error) => {
              return of([]);
            }),
          );
        } else {
          this[fieldName] = [];
          this.isLoadingResults = false;
          this.changeDetectorRef.detectChanges();
          return [];
        }
      }),
      finalize(() => {
        this.isLoadingResults = false;
        this.changeDetectorRef.detectChanges();
      }),
    );
    switch (type) {
      case FilterEntities.TAG:
        this.processTagAutocomplete(autoCompleteRes$);
        break;
      case FilterEntities.SKU:
        this.processSkuAutocomplete(autoCompleteRes$);
        break;
      case FilterEntities.DELIVERY_METHOD:
        this.processDeliveryMethodAutocomplete(autoCompleteRes$);
        break;
    }
  }

  private processDeliveryMethodAutocomplete(res$) {
    const subscription = res$.subscribe((res: DeliveryMethodType) => {
      this.deliveryMethods = res;
    });

    this.deliveryMethodsControlsSubscriptions$.push(subscription);
  }

  private processSkuAutocomplete(res$) {
    const subscription = res$.subscribe((res: TagResponseType | SkusResponseType) => {
      this.skus = (res as SkusResponseType).skus;
    });
    this.skusControlsSubscriptions$.push(subscription);
  }

  private processTagAutocomplete(res$) {
    const subscription = res$.subscribe((res: TagResponseType | SkusResponseType) => {
      this.tags = (res as TagResponseType).orderTags
        .map((tag) => ({
          name: tag.name,
          type: 'tableOrderTag',
        }))
        .concat(
          (res as TagResponseType).productTags.map((tag) => ({
            name: tag.name,
            type: 'tableProductTag',
          })),
        );
    });
    this.tagsControlsSubscriptions$.push(subscription);
  }

  initDropDownOptions() {
    this.inventoryLocationOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: 'inventoryLocation',
      labelField: 'storeName',
    });

    this.packagesOptions[0][0] = this.ruleTemplateService.initialPackagesOptions(
      this.ruleTemplateService,
      this.conditions,
    );

    this.isFiltersOptions[0][0] = this.initialFiltersOptions('isFilteredBy');
    this.isNotFiltersOptions[0][0] = this.initialFiltersOptions('isNotFilteredBy');

    this.deliveryMethodOptions[0][0] = this.ruleTemplateService.convertValuesToDropDownOptions({
      labelField: 'label',
      valueField: 'value',
      typeField: 'type',
      conditionName: 'deliveryMethod',
      conditions: this.conditions[0].values,
      multiSelect: true,
    });
    this.colorCodingOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.actions[0].values,
      conditionName: 'setColorCoding',
      labelField: 'label',
      valueField: 'value',
    });
    this.currentColorCodingOperator = this.colorCodingOptions[0];
    this.createdWeekDaysOptions[0][0] = this.ruleTemplateService.initCreatedOnOptions(this.conditions);
    this.shipOnDateOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: RulesConditions.ShipOnDate,
      labelField: 'label',
      valueField: 'value',
    });
    this.timeWindowWeekDaysOptions[0][0] = this.ruleTemplateService.initTimeWindowWeekDaysOptions(this.conditions);
  }

  initialFiltersOptions(conditionName) {
    return this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: conditionName,
      labelField: 'name',
      multiSelect: true,
    });
  }

  onDeliveryMethodSelect(indices: [number, number], method: string) {
    const [j, i] = indices;
    const currentConditionsValue = this.currentAllConditionsSelectValue[j][i];
    this.controls[j][i].reset('', { onlySelf: true });
    if (!Array.isArray(this.currentSelectedDeliveryMethods[j][i])) {
      this.currentSelectedDeliveryMethods[j][i] = [];
    }
    this.currentSelectedDeliveryMethods[j][i] = Array.from(
      new Set([...this.currentSelectedDeliveryMethods[j][i], method]),
    );
    this.currentSelectedDeliveryMethods = [...this.currentSelectedDeliveryMethods];
    const newValues = this.currentSelectedDeliveryMethods[j][i].map((value) => ({ value, label: value }));

    this.onChangeConditionSelect.emit({
      value: {
        ...currentConditionsValue,
        value: { operator: this.currentOperatorValue[j][i], values: newValues },
      },
      index: [j, i],
    });
  }

  onDeliveryMethodUnSelect(indices: [number, number], methodIndex) {
    const [j, i] = indices;
    const currentMethods = [...(this.currentSelectedDeliveryMethods[j][i] || [])];
    currentMethods.splice(methodIndex, 1);
    this.currentSelectedDeliveryMethods[j][i] = [...currentMethods];
    this.currentSelectedDeliveryMethods = [...this.currentSelectedDeliveryMethods];
    const newValues = this.currentSelectedDeliveryMethods[j][i].map((value) => ({ value, label: value }));

    this.onChangeConditionSelect.emit({
      value: {
        ...this.currentAllConditionsSelectValue[j][i],
        value: {
          operator: this.currentOperatorValue[j][i],
          values: newValues,
        },
      },
      index: [j, i],
    });
  }

  getSelectedConditions() {
    if (!this.selectedRuleParams) {
      return [[null]];
    }

    const selectedConditions = [[]];
    const orWhenLength = this.selectedRuleParams.orWhen.length;

    for (let j = 0; j < orWhenLength; j++) {
      this.addOrCondition(j, false);
      for (let i = 0; i < this.selectedRuleParams.orWhen[j].andWhen.length; i++) {
        this.addCondition([j, i], false);
        const andWhenName: string = Object.keys(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValue: any = Object.values(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValueToCheck = andWhenValue.values ? andWhenValue.values : andWhenValue;
        const condition = this.conditions[0].values.find(
          findConditionByNameAndValue.bind(null, andWhenName, andWhenValueToCheck),
        );

        if (!selectedConditions[j]) {
          selectedConditions[j] = [];
        }
        selectedConditions[j].push(condition);

        switch (andWhenName) {
          case 'deliveryMethod':
            // Old format
            if (typeof andWhenValue !== 'object') {
              const selectedOption = (this.deliveryMethodOptions as any[]).find((opt) => opt.value === andWhenValue);
              const operator = condition.operators.find((operator: RulesOperator) => operator.value == 'is');
              this.currentOperatorValue[j][i] = operator;
              if (selectedOption) {
                selectedOption.checked = true;
                this.currentDeliveryMethodsSelectValue[j][i] = selectedOption;
                const value = { operator: operator, is: operator.value === 'is', values: [selectedOption] };
                this.onChangeConditionSelect.emit({ value: { ...condition, value: value }, index: [j, i] });
              }
            } else {
              this.currentDeliveryMethodsSelectValue[j][i] = andWhenValue.values.map((val) => ({
                ...val,
                checked: true,
              }));
              this.currentSelectedDeliveryMethods[j][i] = andWhenValue.values.map((val) => val.value);
              this.currentDeliveryMethodInputValue[j][i] = this.currentSelectedDeliveryMethods[j][i].join(', ');
              const operator = andWhenValue.operator
                ? andWhenValue.operator
                : condition.operators.find((operator: RulesOperator) => operator.value == 'is');
              this.currentOperatorValue[j][i] = operator;
            }
            break;
          case 'orderTag':
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            if (andWhenValue.operator2) {
              this.currentOperator2Value[j][i] = andWhenValue.operator2;
            }
            if (andWhenValue.selectedTags) {
              this.currentSelectedTags[j][i] = andWhenValue.selectedTags;
            }
            if (andWhenValue.value) {
              this.currentTagsValue[j][i] = andWhenValue.value;
            }
            break;
          case 'orderSku':
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            if (andWhenValue.operator2) {
              this.currentOperator2Value[j][i] = andWhenValue.operator2;
            }
            if (andWhenValue.selectedSkus) {
              this.currentSelectedSkus[j][i] = andWhenValue.selectedSkus;
            }
            if (andWhenValue.value) {
              this.currentSkusValue[j][i] = andWhenValue.value;
            }
            break;
          case 'totalOrderWeight':
            this.currentWeightValue[j][i] = andWhenValue.amount;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'totalOrderValue':
            this.currentTotalValueVal[j][i] = andWhenValue.amount;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'itemQuantity':
            this.currentItemQuantityValue[j][i] = andWhenValue.amount;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'inventoryLocation':
            this.currentInventoryLocationValue[j][i] = andWhenValue.address;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'boxChosen':
            this.currentPackageValue[j][i] = andWhenValue.boxesArray;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'isFilteredBy':
            this.currentIsFilterValue[j][i] = andWhenValue.filtersArray;
            break;
          case 'isNotFilteredBy':
            this.currentIsNotFilterValue[j][i] = andWhenValue.filtersArray;
            break;
          case RulesConditions.CreatedOn:
            this.currentCreatedOnValue[j][i] = andWhenValue;
            break;
          case RulesConditions.ShipOnDate:
            this.currentShipOnDateValue[j][i] = andWhenValue;
          case RulesConditions.Time:
            this.currentTimeWindowValue[j][i] = andWhenValue;
            this.currentOperatorValue[j][i] = andWhenValue.operator;
            break;
          case 'shippingAddressState':
            this.currentStateSelectValue[j][i] = andWhenValue.states;
            const stateOperator =
              andWhenValue.operator ?? condition.operators.find((operator: RulesOperator) => operator.value == 'is');
            this.currentOperatorValue[j][i] = stateOperator;
            break;
        }

        this.controls[j][i] = new UntypedFormControl();
        this.initSearchTerm([j, i], FilterEntities.TAG);
        this.initSearchTerm([j, i], FilterEntities.SKU);
        this.initSearchTerm([j, i], FilterEntities.DELIVERY_METHOD);
      }
    }

    return selectedConditions;
  }

  changeConditionSelect({ value: selectedCondition }, indices: [number, number]): void {
    const [j, i] = indices;
    const updatedSelectedCondition = { ...selectedCondition };

    switch (selectedCondition.name) {
      case 'deliveryMethod':
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        this.currentDeliveryMethodsSelectValue[j][i] = undefined;
        break;
      case 'shippingAddressCountry':
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        break;
      case 'totalOrderWeight':
        const updatedWeightOptions = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators,
          i,
          RulesAmountType.WEIGHT,
          this.currentAllConditionsSelectValue[j],
          this.currentOperatorValue[j],
        );
        this.currentOperatorValue[j][i] = updatedWeightOptions.find((option: DropdownOption) => !option.disabled);
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        break;
      case 'itemQuantity':
        const updatedItemsOptions = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators,
          i,
          RulesAmountType.ITEM_QUANTITY,
          this.currentAllConditionsSelectValue[j],
          this.currentOperatorValue[j],
        );
        this.currentOperatorValue[j][i] = updatedItemsOptions.find((option: DropdownOption) => !option.disabled);
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        break;
      case 'inventoryLocation':
        const address = this.inventoryLocationOptions[0];
        this.currentInventoryLocationValue[j][i] = address;
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i], address };
        break;
      case 'boxChosen':
        const boxesArray = [];
        this.currentPackageValue[j][i] = boxesArray;
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i], boxesArray };
        break;
      case 'isFilteredBy':
      case 'isNotFilteredBy':
        const filtersArray = [];
        selectedCondition.name == 'isFilteredBy'
          ? (this.currentIsFilterValue[j][i] = [])
          : (this.currentIsNotFilterValue[j][i] = []);
        updatedSelectedCondition.value = { filtersArray };
        break;
      case 'orderTag':
        const updatedOrderTagsOperatorOptions = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators,
          i,
          RulesAmountType.TAGS,
          this.currentAllConditionsSelectValue[j],
          this.currentOperatorValue[j],
        );
        this.currentOperatorValue[j][i] = updatedOrderTagsOperatorOptions.find(
          (option: DropdownOption) => !option.disabled,
        );
        const updatedOrderTagsOperator2Options = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators2,
          i,
          RulesAmountType.TAGS,
          this.currentAllConditionsSelectValue[j],
          this.currentOperator2Value[j],
        );

        this.currentOperator2Value[j][i] = updatedOrderTagsOperator2Options.find(
          (option: DropdownOption) => !option.disabled,
        );
        break;
      case 'orderSku':
        const updatedOrderSkuOperatorOptions = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators,
          i,
          RulesAmountType.SKUS,
          this.currentAllConditionsSelectValue[j],
          this.currentOperatorValue[j],
        );
        this.currentOperatorValue[j][i] = updatedOrderSkuOperatorOptions.find(
          (option: DropdownOption) => !option.disabled,
        );
        const updatedOrderSkusOperator2Options = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators2,
          i,
          RulesAmountType.SKUS,
          this.currentAllConditionsSelectValue[j],
          this.currentOperator2Value[j],
        );

        this.currentOperator2Value[j][i] = updatedOrderSkusOperator2Options.find(
          (option: DropdownOption) => !option.disabled,
        );
        break;
      case 'totalOrderValue':
        const updatedValueOptions = this.ruleTemplateService.prepareOperatorOptions(
          selectedCondition.operators,
          i,
          RulesAmountType.TOTAL_VALUE,
          this.currentAllConditionsSelectValue[j],
          this.currentOperatorValue[j],
        );
        this.currentOperatorValue[j][i] = updatedValueOptions.find((option: DropdownOption) => !option.disabled);
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        break;
      case RulesConditions.CreatedOn:
        this.currentCreatedOnValue[j][i] = {
          weekDays: [],
          from: this.timeOfDayOptions[0],
          to: this.timeOfDayOptions[this.timeOfDayOptions.length - 1],
          timezone: this.shopTimeZone,
        };
        updatedSelectedCondition.value = this.currentCreatedOnValue[j][i];
        break;
      case RulesConditions.Time:
        this.currentTimeWindowValue[j][i] = {
          weekDays: [],
          from: this.timeWindowOfDayOptions[0],
          to: this.timeWindowOfDayOptions[this.timeWindowOfDayOptions.length - 1],
          timezone: this.shopTimeZone,
        };
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = {
          operator: this.currentOperatorValue[j][i],
          ...this.currentTimeWindowValue[j][i],
        };
        break;
      case 'shippingAddressState':
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i] };
        break;
      case RulesConditions.ShipOnDate:
        const value = this.shipOnDateOptions[0];
        // this.currentShipOnDateValue[j][i] = value;
        this.currentOperatorValue[j][i] = selectedCondition.operators[0];
        updatedSelectedCondition.value = { operator: this.currentOperatorValue[j][i], value };
        break;
    }

    selectedCondition = updatedSelectedCondition;
    this.onChangeConditionSelect.emit({ value: selectedCondition, index: [j, i] });
    this.onResetActionSelectValue();
  }

  selectState({ value: selectedStates }, indices: [number, number]): void {
    const [j, i] = indices;

    if (!selectedStates) {
      return;
    }
    const shippingAddressStateCondition = {
      ...this.currentAllConditionsSelectValue[j][i],
    };
    const states = selectedStates;
    const operator = this.currentOperatorValue[j][i];
    shippingAddressStateCondition.value = {
      operator,
      states,
    };
    this.onChangeConditionSelect.emit({ value: shippingAddressStateCondition, index: [j, i] });
  }

  selectDeliveryMethod(selectedMethod, indices: [number, number]): void {
    const [j, i] = indices;

    if (!selectedMethod) {
      return;
    }
    const deliveryMethodsCondition = {
      ...this.currentAllConditionsSelectValue[j][i],
    };

    const is = this.currentOperatorValue[j][i].value === 'is';
    const operator = this.currentOperatorValue[j][i];

    const values = selectedMethod
      .filter((method) => method.checked)
      .map((method) => ({ value: method.value, label: method.label, checked: true }));

    deliveryMethodsCondition.value = {
      operator,
      is,
      values,
    };

    this.onChangeConditionSelect.emit({ value: deliveryMethodsCondition, index: [j, i] });
    this.currentDeliveryMethodsSelectValue[j][i] = values;
  }

  changeOperatorSelect(event, indices: [number, number]) {
    const [j, i] = indices;
    let operator;
    let is;

    if (event?.source?.controlType == 'mat-select') {
      if (event.value == 'is') {
        operator = { value: 'is', label: 'is' };
        is = true;
      }
      if (event.value == 'is-not') {
        operator = { value: 'is-not', label: 'is not' };
        is = false;
      }
    } else {
      operator = event;
    }

    this.currentSelectedDeliveryMethods[j][i] = [];
    this.currentSelectedDeliveryMethods = [...this.currentSelectedDeliveryMethods];

    this.currentOperatorValue[j][i] = operator;
    this.onChangeOperatorSelect.emit({ operator, is, index: [j, i] });
  }

  changeOperator2Select(operator, indices: [number, number]) {
    const [j, i] = indices;
    this.currentOperator2Value[j][i] = operator;
    this.onChangeOperator2Select.emit({ operator, index: [j, i] });
  }

  onAmountTyped(amountTyped: number, indices: [number, number], type: RulesAmountType): void {
    const [j, i] = indices;
    const amountCondition = this.ruleTemplateService.onAmountTyped(
      amountTyped,
      i,
      type,
      this.currentAllConditionsSelectValue[j],
      this.currentOperatorValue[j],
      this.currentWeightValue[j],
      this.currentItemQuantityValue[j],
      this.currentTotalValueVal[j],
    );
    this.onChangeConditionSelect.emit({ value: amountCondition, index: [j, i] });
  }

  changeInventorySelect(value, indices: [number, number]): void {
    const [j, i] = indices;
    const inventoryLocationCondition = this.ruleTemplateService.changeInventorySelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentOperatorValue[j],
    );
    this.onChangeConditionSelect.emit({ value: inventoryLocationCondition, index: [j, i] });
  }

  changePackageSelect(value, indices: [number, number]): void {
    const [j, i] = indices;
    const packageCondition = this.ruleTemplateService.changePackageSelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentOperatorValue[j],
      this.currentPackageValue[j],
    );
    this.onChangeConditionSelect.emit({ value: packageCondition, index: [j, i] });
  }

  changeFilterSelect(value, indices: [number, number], conditionName): void {
    const [j, i] = indices;
    const filterCondition: any = {
      ...this.currentAllConditionsSelectValue[j][i],
    };
    const fieldName =
      conditionName == 'isFilteredBy'
        ? 'currentIsFilterValue'
        : conditionName == 'isNotFilteredBy'
          ? 'currentIsNotFilterValue'
          : '';
    let filtersArray = this[fieldName][j][i] || [];
    if (value.checked) {
      filtersArray.push(value);
    } else {
      filtersArray = filtersArray.filter((box) => box.value != value.value);
    }
    this[fieldName][j][i] = filtersArray;
    filterCondition.value = {
      filtersArray,
    };
    this.onChangeConditionSelect.emit({ value: filterCondition, index: [j, i] });
  }

  onReturnPreferencesFormInit(form: UntypedFormGroup) {
    if (this.currentActionSelectValue.name == 'setReturnPreferences') {
      const firstValue = form.value;
      this.onChangeActionSelect.emit({ ...this.currentActionSelectValue, value: firstValue });
    }
    form.valueChanges.subscribe((value) => {
      if (this.currentActionSelectValue.name == 'setReturnPreferences') {
        this.onChangeActionSelect.emit({ ...this.currentActionSelectValue, value: value });
      }
    });
  }

  setDeliveryServiceLevels(serviceLevels) {
    if (serviceLevels.find((item) => item.serviceLevel_type)) {
      this.deliveryServiceLevels = [...serviceLevels];
      this.currentServiceLevelSelectValue = serviceLevels.some(
        (x) => x.serviceLevel_id === this.currentServiceLevelSelectValue,
      )
        ? this.currentServiceLevelSelectValue
        : serviceLevels[0].serviceLevel_id;
    } else {
      this.deliveryServiceLevels = null;
      this.currentServiceLevelSelectValue = null;
    }
  }

  selectDeliveryServiceLevel({ value: serviceLevelId }): void {
    this.currentServiceLevelSelectValue = serviceLevelId;
    if (this.currentActionSelectValue.name == 'batchImmediatelyAndAssign') {
      this.selectDeliveryService({
        value: this.currentDeliveryServiceSelectValue,
      });
    } else {
      this.selectDeliveryServiceOnSpecificTime({
        value: this.currentDeliveryServiceSelectValue,
      });
    }
  }

  selectDeliveryService({ value: deliveryServiceId }): void {
    const deliveryServiceAction = {
      ...this.currentActionSelectValue,
    };
    deliveryServiceAction.value = {
      ...this.currentActionSelectValue.values.find((item: any) => item.id == deliveryServiceId),
    };
    this.setDeliveryServiceLevels(deliveryServiceAction.value.serviceLevels);
    if (this.deliveryServiceLevels) {
      deliveryServiceAction.value.serviceLevel = {
        ...this.deliveryServiceLevels.find((item) => item.serviceLevel_id == this.currentServiceLevelSelectValue),
      };
    }
    if (this.batchName) {
      deliveryServiceAction.value.batchName = this.batchName;
    }
    this.onChangeActionSelect.emit(deliveryServiceAction);
  }

  batchNameChanged(batchName: string) {
    this.batchName = batchName;
    if (this.currentActionSelectValue.name == 'batchImmediatelyAndAssign') {
      if (this.currentDeliveryServiceSelectValue) {
        this.selectDeliveryService({
          value: this.currentDeliveryServiceSelectValue,
        });
      }
    } else {
      this.selectDeliveryServiceOnSpecificTime({
        value: this.currentDeliveryServiceSelectValue,
      });
    }
  }

  onTeamNoteChanged(newValues) {
    const teamNoteAction = {
      ...this.currentActionSelectValue,
    };

    this.currentTeamNote = newValues;
    teamNoteAction.value = newValues;
    this.onChangeActionSelect.emit(teamNoteAction);
  }

  onShipmetExtrasChanged(newValues) {
    const shipmentExtrasAction = {
      ...this.currentActionSelectValue,
    };
    const valueToDisplay = Object.entries(newValues).map((entry) => {
      const valueFullObj = shipmentExtrasAction.values.find((v) => v.fieldName == entry[0]);
      const picked = (({ fieldName, displayName }) => ({ fieldName, displayName }))(valueFullObj) as any;
      picked.value = entry[1];
      return picked;
    });

    shipmentExtrasAction.value = { shipmentExtrasFields: newValues, valuesObj: valueToDisplay };
    this.onChangeActionSelect.emit(shipmentExtrasAction);
  }

  changeShipOnDateSelect(event, indices: [number, number]) {
    const [j, i] = indices;
    const shipOnDateCondition = this.ruleTemplateService.changeShipOnDateSelect(
      event,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentOperatorValue[j],
      this.currentShipOnDateValue[j][i],
    );
    this.currentShipOnDateValue[j][i] = shipOnDateCondition.value;
    this.onChangeConditionSelect.emit({ value: shipOnDateCondition, index: [j, i] });
  }

  onShipmentPreferencesChanged(newValues) {
    // todo: rename carrier in shipmentPreferncesFields to carriersAndProviders
    const recommendedCarrierAction = {
      ...this.currentActionSelectValue,
    };
    this.currentRecommendedCarrier = newValues;
    recommendedCarrierAction.value = {
      shipmentPreferncesFields: newValues,
      shipOn: this.currentShipOnValue,
    };
    this.onChangeActionSelect.emit(recommendedCarrierAction);
  }

  onSmartShipOnChanged(newValues, type: 'shipmentPreferncesFields' | 'rightOnTime' | 'bufferDelayRisk' | 'delayDays') {
    const smartShipOnAction = {
      ...this.currentActionSelectValue,
    };
    switch (type) {
      case 'shipmentPreferncesFields':
        this.currentSmartShipOn = { ...this.currentSmartShipOn, shipmentPreferncesFields: newValues };
        break;
      case 'rightOnTime':
        this.currentSmartShipOn = { ...this.currentSmartShipOn, rightOnTime: true, bufferDelayRisk: { value: false } };
        break;
      case 'bufferDelayRisk':
        this.currentSmartShipOn = {
          ...this.currentSmartShipOn,
          bufferDelayRisk: { value: true, delayDays: 1 },
          rightOnTime: false,
        };
        break;
      case 'delayDays':
        this.currentSmartShipOn = {
          ...this.currentSmartShipOn,
          bufferDelayRisk: { value: true, delayDays: newValues },
        };
        break;
    }
    smartShipOnAction.value = {
      ...this.currentSmartShipOn,
    };
    this.onChangeActionSelect.emit(smartShipOnAction);
  }

  getSelectedAction() {
    if (!this.selectedRuleParams) {
      return null;
    }
    const selectedActionName = Object.keys(this.selectedRuleParams.andThen[0])[0];
    const selectedActionValue: any = Object.values(this.selectedRuleParams.andThen[0])[0];
    const actionData = this.actions[0]?.values?.find((a) => a.name === selectedActionName);

    switch (selectedActionName) {
      case RulesActions.BatchAndAsign:
        this.currentDeliveryServiceSelectValue = selectedActionValue.id;
        this.currentServiceLevelSelectValue = selectedActionValue.serviceLevel
          ? selectedActionValue.serviceLevel.serviceLevel_id
          : null;
        this.batchName = selectedActionValue.batchName;
        break;
      case RulesActions.BatchAndAsignSpecificTime:
        this.currentBatchUpToSelectValue = selectedActionValue.batchUpToMode;
        this.currentBatchUpToX = selectedActionValue.batchUpToCount;
        this.currentDeliveryServiceSelectValue = selectedActionValue.deliveryService.id;
        this.currentServiceLevelSelectValue = selectedActionValue.deliveryService.serviceLevel
          ? selectedActionValue.deliveryService.serviceLevel.serviceLevel_id
          : null;
        this.currentWeekDaysValue = selectedActionValue.weekDays;
        this.currentTimePickerValue = selectedActionValue.atTime;
        this.batchName = selectedActionValue.batchName;
        break;

      case RulesActions.SetReturnPreferences:
        this.initialReturnPreferences = {
          id: null,
          active: true,
          box: selectedActionValue.box,
          preferredServices: selectedActionValue.preferredServices,
          preferredProviders: selectedActionValue.preferredProviders,
          returnToAddress: selectedActionValue.returnToAddress,
          enforceCarrierSelection: selectedActionValue.enforceCarrierSelection,
          replaceOriginalTrackingNumber: selectedActionValue.replaceOriginalTrackingNumber,
        };
        this.initReturnPreferencesOptions(actionData);
      case RulesActions.SetRecommendedCarrier:
        this.currentShipOnValue = selectedActionValue.shipOn?.shipOn ? selectedActionValue.shipOn : {};
        this.currentRecommendedCarrier = selectedActionValue.shipmentPreferncesFields;
        this.carriersData = actionData.values;
        this.initDeliveryDateOptions(actionData, 'shipOnValues');
        break;
      case RulesActions.Snooze:
        this.currentSnoozeSelectValue = {
          snoozeBy: selectedActionValue.snoozeBy,
          data: selectedActionValue.snoozeBy,
          x: selectedActionValue.x,
          before: selectedActionValue.before,
        };
        this.initDeliveryDateOptions(
          this.actions[0]?.values?.find((a) => a.name === 'snoozeOrder'),
          'values',
        );
        break;
      case RulesActions.ShipmentExtras:
        this.currentShipmentExtras = selectedActionValue;
        break;
      case RulesActions.TeamNote:
        this.currentTeamNote = selectedActionValue;
        break;
      case RulesActions.DeliveryMethod:
        this.deliveryMethod = selectedActionValue;
        break;
      case RulesActions.ColorCoding:
        this.currentColorCoding = selectedActionValue.colors;
        this.currentColorCodingOperator = selectedActionValue.operator;
        break;
      case RulesActions.SplitOrderByStrategy:
        this.splitOrderData = selectedActionValue;
        break;
      case RulesActions.AddTags:
        this.currentActionAddTagsValue = selectedActionValue;
        break;
      case RulesActions.DeleteTags:
        this.currentActionDeleteTagsValue = selectedActionValue;
        break;
      case RulesActions.SmartShipOn:
        this.currentSmartShipOn = selectedActionValue;
        this.carriersData = actionData.values;
        break;
    }
    return this.actions[0].values.find(findActionByNameAndValue.bind(null, selectedActionName, selectedActionValue));
  }

  initReturnPreferencesOptions(selectedAction) {
    this.shopCarriers = selectedAction.values.map((carrier) => {
      const providers = carrier.providers.map((provider) => ({
        ...provider,
        id: provider.provider_id,
        name: provider.provider_name,
      }));
      return { ...carrier, providers };
    });
  }

  changeActionSelect({ value: selectedAction }): void {
    switch (selectedAction.name) {
      case RulesActions.TeamNote:
        this.currentActionSelectValue = selectedAction;
        this.onTeamNoteChanged(this.currentTeamNote);
        break;
      case RulesActions.SetRecommendedCarrier:
        this.carriersData = selectedAction.values;
        this.initDeliveryDateOptions(selectedAction, 'shipOnValues');
        break;
      case RulesActions.Snooze:
        this.initDeliveryDateOptions(selectedAction, 'values');
        break;
      case RulesActions.SetReturnPreferences:
        this.initReturnPreferencesOptions(selectedAction);
        break;
      case RulesActions.SmartShipOn:
        this.carriersData = selectedAction.values;
        break;
      default:
        // do not set for multiple values
        if (!selectedAction.values) {
          this.onChangeActionSelect.emit(selectedAction);
        }
    }
  }

  selectBatchUpToChange(): void {
    this.selectDeliveryServiceOnSpecificTime({
      value: this.currentDeliveryServiceSelectValue,
    });
  }

  currentBatchUpToXChange(currentBatchUpToX): void {
    this.currentBatchUpToX = currentBatchUpToX;
    this.selectDeliveryServiceOnSpecificTime({
      value: this.currentDeliveryServiceSelectValue,
    });
  }

  selectWeekDay(): void {
    this.selectDeliveryServiceOnSpecificTime({
      value: this.currentDeliveryServiceSelectValue,
    });
  }

  setCurrentTimePickerValue(timeValue) {
    this.currentTimePickerValue = timeValue;
    this.selectDeliveryServiceOnSpecificTime({
      value: this.currentDeliveryServiceSelectValue,
    });
  }

  selectDeliveryServiceOnSpecificTime({ value: deliveryServiceId }): void {
    if (!deliveryServiceId) {
      return;
    }
    const deliveryServiceAction = {
      ...this.currentActionSelectValue,
    };
    const deliveryService = {
      ...this.currentActionSelectValue.values.find((item: any) => item.id == deliveryServiceId),
    };

    this.setDeliveryServiceLevels(deliveryService.serviceLevels);

    if (this.deliveryServiceLevels) {
      deliveryService.serviceLevel = {
        ...this.deliveryServiceLevels.find((item) => item.serviceLevel_id == this.currentServiceLevelSelectValue),
      };
    }

    const batchUpToMode = this.currentBatchUpToSelectValue;
    const batchUpToCount = this.currentBatchUpToX;
    const weekDays = this.currentWeekDaysValue;
    const atTime = this.currentTimePickerValue;

    if (!deliveryService || !atTime || !batchUpToMode || (batchUpToMode == 'up-to' && !batchUpToCount)) {
      // cancel deliveryServiceAction if some value is absent
      return;
    }

    deliveryServiceAction.value = {
      deliveryService,
      batchUpToMode,
      batchUpToCount,
      weekDays,
      atTime,
    };
    if (this.batchName) {
      deliveryServiceAction.value.batchName = this.batchName;
    }
    // update selected action value
    this.onChangeActionSelect.emit(deliveryServiceAction);
  }

  snoozeChanged(selectedValue: any) {
    const snoozeAction = {
      ...this.currentActionSelectValue,
    };
    const currentValue = (this.currentSnoozeSelectValue = { ...selectedValue, snoozeBy: selectedValue.data });
    snoozeAction.value = currentValue;
    this.onChangeActionSelect.emit(snoozeAction);
  }

  shipOnChanged(selectedValue: any) {
    const shipOnAction = {
      ...this.currentActionSelectValue,
    };
    this.currentShipOnValue = { ...selectedValue, shipOn: selectedValue.data };

    shipOnAction.value = {
      shipOn: this.currentShipOnValue,
      shipmentPreferncesFields: this.currentRecommendedCarrier,
    };
    this.onChangeActionSelect.emit(shipOnAction);
  }

  getProvidersDisplayName(providerName) {
    if (!providerName) {
      return;
    }
    return providerName[0]?.toUpperCase() + providerName.substr(1).toLowerCase();
  }

  getFormattedText(text: string) {
    return text.replace(/\[\[/g, '<span class="highlight">').replace(/\]\]/g, '</span>');
  }

  getActionText(action: AutomationRulesActions.SelectedAction) {
    let text = `<span class="value">${action.text}</span>`;
    switch (action.name) {
      case RulesActions.BatchAndAsign:
        text = action.text.replace('[[delivery service]]', 'delivery service');
        break;
      case RulesActions.SetRecommendedCarrier:
        text = action.text.replace('[[carrier]]', 'carrier');
        break;
      case RulesActions.Snooze:
        text = action.text.slice(0, action.text.indexOf('[[')).trim();
        break;
      case RulesActions.TeamNote:
        text = 'Add team note with the following message';
        break;
    }

    return text;
  }

  onResetActionSelectValue() {
    this.currentActionSelectValue = null;
    this.currentDeliveryServiceSelectValue = null;
    this.currentServiceLevelSelectValue = null;
    this.batchName = null;
    this.deliveryServiceLevels = null;
    this.currentShippingDays = null;
    this.currentProvidersSelectValue = null;
    this.deliveryServiceProviders = null;
    this.providerServices = null;
    this.currentBatchUpToSelectValue = 'all';
    this.currentBatchUpToX = 1;
    this.currentWeekDaysValue = [];
    this.weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    this.currentTimePickerValue = '8:00 AM';
    this.currentSnoozeSelectValue = {};
    this.currentShipOnValue = {};
    this.currentRecommendedCarrier = {};
    this.currentActionAddTagsValue = [];
    this.currentActionDeleteTagsValue = [];
    this.currentTeamNote = {};
    this.currentColorCoding = [ColorEnum.NO_COLOR];
    this.currentColorCodingOperator = this.colorCodingOptions[0];
    this.currentSmartShipOn = {};
    this.onChangeActionSelect.emit(this.currentActionSelectValue);
  }

  setHeadsUpNotifications(action: any) {
    this.headsUp = this.ruleTemplateService.setHeadsUpNotifications(action, this.currentActionSelectValue);
  }

  getServiceLabelDisplayName(serviceLevelType) {
    return ServiceLevelDisplayNames[serviceLevelType] || serviceLevelType;
  }

  onTagSelect(indices: [number, number], tag: RulesTag) {
    const [j, i] = indices;
    const { optionExists, tagsCondition } = this.ruleTemplateService.onTagSelect(
      i,
      tag,
      this.controls[j],
      this.currentAllConditionsSelectValue[j],
      this.currentSelectedTags[j],
      this.currentOperatorValue[j],
      this.currentOperator2Value[j],
    );
    if (!optionExists) {
      const currentTagInput = this.tagsInputs.find((tagInput: any) => tagInput.id == 'tagsInput' + j + i);
      currentTagInput?.input?.nativeElement?.focus();
      this.onChangeConditionSelect.emit({ value: tagsCondition, index: [j, i] });
    }
  }

  onTagRemove(conditionIndices: [number, number], tagIndex: number): void {
    const [j, i] = conditionIndices;
    const tagsCondition = this.ruleTemplateService.onTagRemove(
      i,
      tagIndex,
      this.currentAllConditionsSelectValue[j],
      this.currentSelectedTags[j],
      this.currentOperatorValue[j],
      this.currentOperator2Value[j],
    );
    this.onChangeConditionSelect.emit({ value: tagsCondition, index: [j, i] });
  }

  onSkuSelect(indices: [number, number], sku: string) {
    const [j, i] = indices;
    const { optionExists, skusCondition } = this.ruleTemplateService.onSkuSelect(
      i,
      sku,
      this.controls[j],
      this.currentAllConditionsSelectValue[j],
      this.currentSelectedSkus[j],
      this.currentOperatorValue[j],
      this.currentOperator2Value[j],
    );
    if (!optionExists) {
      const currentSkuInput = this.skusInputs.find((skuInput: any) => skuInput.id == 'skuInput' + j + i);
      currentSkuInput?.input?.nativeElement?.focus();
      this.onChangeConditionSelect.emit({ value: skusCondition, index: [j, i] });
    }
  }

  onSkuRemove(conditionIndices: [number, number], skuIndex: number): void {
    const [j, i] = conditionIndices;
    const skusCondition = this.ruleTemplateService.onSkuRemove(
      i,
      skuIndex,
      this.currentAllConditionsSelectValue[j],
      this.currentSelectedSkus[j],
      this.currentOperatorValue[j],
      this.currentOperator2Value[j],
    );
    this.onChangeConditionSelect.emit({ value: skusCondition, index: [j, i] });
  }

  onOrderTagConditionChanged(value, indices: [number, number]) {
    const [j, i] = indices;
    this.currentTagsValue[j][i] = value;
    const orderTagCondition = {
      ...this.currentAllConditionsSelectValue[j][i],
    };
    orderTagCondition.value = {
      value,
      operator: this.currentOperatorValue[j][i],
      operator2: this.currentOperator2Value[j][i],
    };

    this.onChangeConditionSelect.emit({ value: orderTagCondition, index: [j, i] });
  }

  onOrderSkuConditionChanged(value, indices: [number, number]) {
    const [j, i] = indices;
    this.currentSkusValue[j][i] = value;
    const orderCondition = {
      ...this.currentAllConditionsSelectValue[j][i],
    };
    orderCondition.value = {
      value,
      operator: this.currentOperatorValue[j][i],
      operator2: this.currentOperator2Value[j][i],
    };

    this.onChangeConditionSelect.emit({ value: orderCondition, index: [j, i] });
  }

  onOrderCreatedWeekDayConditionChanged(value, indices: [number, number]) {
    const [j, i] = indices;
    const orderCondition = this.ruleTemplateService.changeCreateWeekDateSelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentCreatedOnValue[j],
    );
    this.onChangeConditionSelect.emit({ value: orderCondition, index: [j, i] });
  }

  onOrderCreatedTimeOfDayConditionChanged(value, indices: [number, number], type) {
    const [j, i] = indices;
    const orderCondition = this.ruleTemplateService.changeCreateTimeOfDaySelect(
      value,
      type,
      i,
      this.timeOfDayOptions,
      this.currentAllConditionsSelectValue[j],
      this.currentCreatedOnValue[j],
    );
    this.onChangeConditionSelect.emit({ value: orderCondition, index: [j, i] });
  }

  onTimeWindowOfWeekDayConditionChanged(value, indices: [number, number]) {
    const [j, i] = indices;
    const timeCondition = this.ruleTemplateService.changeCreateWeekDateSelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentTimeWindowValue[j],
    );
    timeCondition.value.operator = this.currentOperatorValue[j][i];
    this.onChangeConditionSelect.emit({ value: timeCondition, index: [j, i] });
  }

  onTimeWindowOfDayConditionChanged(value, indices: [number, number], type) {
    const [j, i] = indices;
    const timeCondition = this.ruleTemplateService.changeCreateTimeOfDaySelect(
      value,
      type,
      i,
      this.timeWindowOfDayOptions,
      this.currentAllConditionsSelectValue[j],
      this.currentTimeWindowValue[j],
    );
    timeCondition.value.operator = this.currentOperatorValue[j][i];
    this.onChangeConditionSelect.emit({ value: timeCondition, index: [j, i] });
  }

  addOrCondition(index, shouldAddChildCondition = true): void {
    this.currentAllConditionsSelectValue.push([]);
    this.currentDeliveryMethodsSelectValue.push([]);
    this.currentTagsValue.push([]);
    this.currentSkusValue.push([]);
    this.currentOperatorValue.push([]);
    this.currentOperator2Value.push([]);
    this.currentSelectedTags.push([[]]);
    this.currentSelectedSkus.push([[]]);
    this.currentWeightValue.push([]);
    this.currentTotalValueVal.push([]);
    this.currentItemQuantityValue.push([]);
    this.currentInventoryLocationValue.push([]);
    this.currentPackageValue.push([]);
    this.currentIsFilterValue.push([]);
    this.currentIsNotFilterValue.push([]);
    this.currentCreatedOnValue.push([
      {
        weekDays: [],
        from: this.timeOfDayOptions[0],
        to: this.timeOfDayOptions[this.timeOfDayOptions.length - 1],
        timezone: this.shopTimeZone,
      },
    ]);
    this.currentTimeWindowValue.push([
      {
        weekDays: [],
        from: this.timeWindowOfDayOptions[0],
        to: this.timeWindowOfDayOptions[this.timeWindowOfDayOptions.length - 1],
        timezone: this.shopTimeZone,
      },
    ]);
    this.currentStateSelectValue.push([]);
    this.currentSelectedDeliveryMethods.push([]);
    this.controls.push([]);
    this.isFiltersOptions.push([[]]);
    this.isNotFiltersOptions.push([[]]);
    this.createdWeekDaysOptions.push([[]]);
    this.timeWindowWeekDaysOptions.push([[]]);
    this.packagesOptions.push([[]]);
    this.currentShipOnDateValue.push([]);
    if (shouldAddChildCondition) {
      this.addCondition([index, 0]);
    }
  }

  addCondition(indices: [number, number], shouldEmit = true): void {
    const [j, i] = indices;
    this.currentSelectedDeliveryMethods[j].push([]);
    this.currentAllConditionsSelectValue[j].push(null);
    this.currentDeliveryMethodsSelectValue[j].push(null);
    this.currentTagsValue[j].push(null);
    this.currentSkusValue[j].push(null);
    this.currentOperatorValue[j].push(null);
    this.currentSelectedTags[j].push([]);
    this.currentSelectedSkus[j].push([]);
    this.controls[j].push(new UntypedFormControl());
    this.initSearchTerm([j, i], FilterEntities.TAG);
    this.initSearchTerm([j, i], FilterEntities.SKU);
    this.initSearchTerm([j, i], FilterEntities.DELIVERY_METHOD);
    this.currentWeightValue[j].push(null);
    this.currentTotalValueVal[j].push(null);
    this.currentItemQuantityValue[j].push(null);
    this.currentInventoryLocationValue[j].push(null);
    this.currentPackageValue[j].push(null);
    this.currentIsFilterValue[j].push(null);
    this.currentIsNotFilterValue[j].push(null);
    this.currentCreatedOnValue[j].push({
      weekDays: [],
      from: this.timeOfDayOptions[0],
      to: this.timeOfDayOptions[this.timeOfDayOptions.length - 1],
      timezone: this.shopTimeZone,
    });
    this.currentTimeWindowValue[j].push({
      weekDays: [],
      from: this.timeWindowOfDayOptions[0],
      to: this.timeWindowOfDayOptions[this.timeWindowOfDayOptions.length - 1],
      timezone: this.shopTimeZone,
    });
    this.packagesOptions[j][i] = this.ruleTemplateService.initialPackagesOptions(
      this.ruleTemplateService,
      this.conditions,
    );

    this.createdWeekDaysOptions[j][i] = this.ruleTemplateService.initCreatedOnOptions(this.conditions);
    this.timeWindowWeekDaysOptions[j][i] = this.ruleTemplateService.initTimeWindowWeekDaysOptions(this.conditions);

    this.isFiltersOptions[j][i] = this.initialFiltersOptions('isFilteredBy');
    this.isNotFiltersOptions[j][i] = this.initialFiltersOptions('isNotFilteredBy');

    if (shouldEmit) {
      this.indexOfLastMatOption = indices;
      this.onChangeConditionSelect.emit({ value: {}, index: [j, i] });
    }
    this.currentShipOnDateValue[j].push(null);
  }

  checkIfAllValuesInserted() {
    const isAllInserted = this.currentAllConditionsSelectValue?.every((conditionGroup, groupIndex) => {
      return conditionGroup.every((currentConditionSelectValue, index) => {
        return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index);
      });
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForConditionGroup(row) {
    const isAllInserted = this.currentAllConditionsSelectValue[row]?.every((currentConditionSelectValue, index) => {
      return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, row, index);
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index) {
    switch (currentConditionSelectValue?.name) {
      case 'deliveryMethod':
        return (
          !!this.currentSelectedDeliveryMethods[groupIndex][index]?.length &&
          !!this.currentOperatorValue[groupIndex][index]
        );
      case 'orderTag':
        return this.isAutoCompleteMode([groupIndex, index])
          ? !!this.currentSelectedTags[groupIndex][index]?.length
          : !!this.currentTagsValue[groupIndex][index];
      case 'totalOrderWeight':
        return !!this.currentWeightValue[groupIndex][index];
      case 'totalOrderValue':
        return !!this.currentTotalValueVal[groupIndex][index];
      case 'itemQuantity':
        return (
          !isNaN(this.currentItemQuantityValue[groupIndex][index]) &&
          this.currentItemQuantityValue[groupIndex][index] >= 0
        );
      case 'inventoryLocation':
        return !!this.currentInventoryLocationValue[groupIndex][index];
      case 'boxChosen':
        return !!this.currentPackageValue[groupIndex][index]?.length;
      case 'isFilteredBy':
        return !!this.currentIsFilterValue[groupIndex][index]?.length;
      case 'isNotFilteredBy':
        return !!this.currentIsNotFilterValue[groupIndex][index]?.length;
      case 'orderSku':
        return this.isAutoCompleteMode([groupIndex, index])
          ? !!this.currentSelectedSkus[groupIndex][index]?.length
          : !!this.currentSkusValue[groupIndex][index];
      case RulesConditions.CreatedOn:
        return this.currentCreatedOnValue[groupIndex][index];
      case RulesConditions.ShipOnDate:
        return this.currentShipOnDateValue[groupIndex][index];
      case RulesConditions.Time:
        return this.currentTimeWindowValue[groupIndex][index];
      case 'shippingAddressState':
        return !!this.currentStateSelectValue[groupIndex][index]?.length;
    }
    return currentConditionSelectValue?.value;
  }

  removeCondition(indices: [number, number]) {
    const [j, i] = indices;
    this.currentAllConditionsSelectValue[j].splice(i, 1);
    this.currentDeliveryMethodsSelectValue[j].splice(i, 1);
    this.currentSelectedTags[j].splice(i, 1);
    this.currentSelectedSkus[j].splice(i, 1);
    this.tagsControlsSubscriptions$[j][i]?.unsubscribe();
    this.skusControlsSubscriptions$[j][i]?.unsubscribe();
    this.controls[j].splice(i, 1);
    this.currentOperatorValue[j].splice(i, 1);
    this.currentWeightValue[j].splice(i, 1);
    this.currentTotalValueVal[j].splice(i, 1);
    this.currentItemQuantityValue[j].splice(i, 1);
    this.currentInventoryLocationValue[j].splice(i, 1);
    this.currentPackageValue[j].splice(i, 1);
    this.currentIsFilterValue[j].splice(i, 1);
    this.currentIsNotFilterValue[j].splice(i, 1);
    this.packagesOptions[j].splice(i, 1);
    this.isFiltersOptions[j].splice(i, 1);
    this.isNotFiltersOptions[j].splice(i, 1);
    this.currentTagsValue[j].splice(i, 1);
    this.currentSkusValue[j].splice(i, 1);
    this.currentCreatedOnValue[j].splice(i, 1);
    this.createdWeekDaysOptions[j].splice(i, 1);
    this.timeWindowWeekDaysOptions[j].splice(i, 1);
    this.currentSelectedDeliveryMethods[j].splice(i, 1);
    if (j > 0 && i == 0 && this.currentAllConditionsSelectValue[j].length == 0) {
      this.currentAllConditionsSelectValue.splice(j, 1);
      this.currentDeliveryMethodsSelectValue.splice(j, 1);
      this.currentSelectedTags.splice(j, 1);
      this.currentSelectedDeliveryMethods.splice(j, 1);
      this.currentSelectedSkus.splice(j, 1);
      this.controls.splice(j, 1);
      this.currentOperatorValue.splice(j, 1);
      this.currentWeightValue.splice(j, 1);
      this.currentTotalValueVal.splice(j, 1);
      this.currentItemQuantityValue.splice(j, 1);
      this.currentInventoryLocationValue.splice(j, 1);
      this.currentPackageValue.splice(j, 1);
      this.currentIsFilterValue.splice(j, 1);
      this.currentIsNotFilterValue.splice(j, 1);
      this.packagesOptions.splice(j, 1);
      this.createdWeekDaysOptions.splice(j, i);
      this.timeWindowWeekDaysOptions.splice(j, i);
      this.isFiltersOptions.splice(j, 1);
      this.isNotFiltersOptions.splice(j, 1);
      this.currentTagsValue.splice(j, 1);
      this.currentSkusValue.splice(j, 1);
      this.currentCreatedOnValue.splice(j, 1);
    }

    this.onRemoveCondition.emit({ index: [j, i] });
  }

  // temporary function until we will change the logic on scheduled rules (especially on backend side in the function batchOnSpecificTimeAndAssign)
  isActionDisabled(currentAction, actionIndex) {
    if (currentAction?.value === 'stepPrint') {
      return !this.currentAllConditionsSelectValue.some((row) =>
        row.some((currentConditionSelectValue) => currentConditionSelectValue?.name == 'fulfilledExternally'),
      );
    }

    if (currentAction?.disabled) {
      return true;
    }
    if (currentAction?.name !== 'batchOnSpecificTimeAndAssign') {
      return false;
    }

    return this.currentAllConditionsSelectValue?.some((row) => {
      return row.some((condition) => {
        return (
          condition?.name != 'orderStatus' &&
          condition?.name != 'deliveryMethod' &&
          condition?.name != 'shippingAddressCountry' &&
          condition?.name != 'totalOrderWeight' &&
          condition?.name != 'itemQuantity' &&
          condition?.name != 'fulfillmentStatus' &&
          condition?.name != 'inventoryLocation' &&
          condition?.name != 'boxChosen' &&
          condition?.name != 'totalOrderValue' &&
          condition?.name != 'orderTag' &&
          condition?.name != 'orderSku' &&
          condition?.name != RulesConditions.CreatedOn &&
          condition?.name != RulesConditions.ShippingState &&
          condition?.name != RulesConditions.ShipOnDate
        );
      });
    });
  }

  onDeliveryMethodChanged(deliveryMethod: string): void {
    const changeDeliveryMethodAction = {
      ...this.currentActionSelectValue,
    };
    changeDeliveryMethodAction.value = deliveryMethod;
    this.onChangeActionSelect.emit(changeDeliveryMethodAction);
  }

  onColorCodingChanged(colors?: string[], operator?: DropdownOption): void {
    if (colors) {
      this.currentColorCoding = colors;
    }
    if (operator) {
      this.currentColorCodingOperator = operator;
    }
    const setColorCodingAction = {
      ...this.currentActionSelectValue,
    };
    setColorCodingAction.value = { colors: this.currentColorCoding, operator: this.currentColorCodingOperator };
    this.onChangeActionSelect.emit(setColorCodingAction);
  }

  isAutoCompleteMode(indices: [number, number]) {
    const [j, i] = indices;
    return ['includes', 'excludes'].includes(this.currentOperatorValue[j][i].value as string);
  }

  isDeliveryMethodAutoCompleteMode(index: [number, number]): boolean {
    const [j, i] = index;
    const operatorValue = this.currentOperatorValue[j][i]?.value;

    switch (operatorValue) {
      case 'is':
      case 'is-not':
        return true;
      case 'contains':
      case 'not-contains':
        return false;
      default:
        return true;
    }
  }

  onDeliveryMethodInputChange(index: [number, number]): void {
    const [j, i] = index;
    const inputValue = this.currentDeliveryMethodInputValue[j][i];

    const deliveryMethods = inputValue
      .split(',')
      .map((method) => method.trim())
      .filter((method) => method.length > 0);

    this.currentSelectedDeliveryMethods[j][i] = deliveryMethods;
    this.currentSelectedDeliveryMethods = [...this.currentSelectedDeliveryMethods];

    const currentConditionsValue = this.currentAllConditionsSelectValue[j][i];
    const formattedValues = deliveryMethods.map((value) => ({ value, label: value }));

    this.onChangeConditionSelect.emit({
      value: {
        ...currentConditionsValue,
        value: { operator: this.currentOperatorValue[j][i], values: formattedValues },
      },
      index: index,
    });
  }

  getDeliveryMethodOperatorText(operator: string): string {
    return this.ruleTemplateService.getDeliveryMethodOperatorText(operator);
  }

  isDuplicateDeliveryMethod(j: number, i: number): boolean {
    const currentInputValue = this.currentDeliveryMethodInputValue[j]?.[i]?.trim();
    if (!currentInputValue) return false;

    const currentValues = this.currentSelectedDeliveryMethods[j]?.[i] || [];
    return currentValues.includes(currentInputValue);
  }

  initDeliveryDateOptions(selectedAction, fieldName) {
    this.deliveryDateOptions = selectedAction[fieldName]?.find((option) => option.value.includes('Before'))?.values;
  }

  tagsChanged(tags: string[], action: 'add' | 'delete') {
    const tagsAction = {
      ...this.currentActionSelectValue,
    };
    switch (action) {
      case 'add':
        this.currentActionAddTagsValue = tags;
        break;
      case 'delete':
        this.currentActionDeleteTagsValue = tags;
        break;
    }
    tagsAction.value = tags;
    this.onChangeActionSelect.emit(tagsAction);
  }
}
